function prefersReduce() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function bootReveal(root) {
  const nodes = [...root.querySelectorAll('[data-reveal]')];
  if (!nodes.length) return () => {};
  if (prefersReduce()) {
    nodes.forEach((node) => node.classList.add('is-in'));
    return () => {};
  }
  const io = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-in');
        io.unobserve(entry.target);
      });
    },
    { threshold: 0.12, rootMargin: '0px 0px -8% 0px' }
  );
  nodes.forEach((node) => io.observe(node));
  return () => io.disconnect();
}

function bootLedger(root) {
  const quotes = [...root.querySelectorAll('[data-voice]')];
  const tabs = [...root.querySelectorAll('[data-voice-tab]')];
  if (!quotes.length) return () => {};

  let index = Math.max(
    0,
    quotes.findIndex((quote) => quote.classList.contains('is-on'))
  );
  if (index < 0) index = 0;

  const live = root.querySelector('[data-voice-live]');
  const autoMs = Number(root.dataset.autoplay) || 0;
  let timer = 0;
  let paused = false;
  // Once a shopper picks a quote, stop auto-rotating so it stays put (also covers touch).
  let userStopped = false;

  const show = (next, { focus, announce } = {}) => {
    index = (next + quotes.length) % quotes.length;
    quotes.forEach((quote, i) => {
      const on = i === index;
      quote.classList.toggle('is-on', on);
      if (quote.hasAttribute('aria-hidden')) quote.setAttribute('aria-hidden', on ? 'false' : 'true');
    });
    tabs.forEach((tab, i) => {
      const on = i === index;
      tab.classList.toggle('is-on', on);
      tab.setAttribute('aria-selected', on ? 'true' : 'false');
      tab.tabIndex = on ? 0 : -1;
    });
    if (live && announce) {
      const name = quotes[index].dataset.name || '';
      live.textContent = name ? `Quote from ${name}` : '';
    }
    if (focus) tabs[index]?.focus();
  };

  const stop = () => {
    if (timer) window.clearInterval(timer);
    timer = 0;
  };

  const start = () => {
    stop();
    if (paused || userStopped || autoMs < 1200 || quotes.length < 2 || prefersReduce()) return;
    timer = window.setInterval(() => show(index + 1), autoMs);
  };

  const onTabClick = (event) => {
    const tab = event.target.closest('[data-voice-tab]');
    if (!tab) return;
    const i = tabs.indexOf(tab);
    if (i < 0) return;
    userStopped = true;
    show(i, { announce: true });
    stop();
  };

  const onKey = (event) => {
    if (!event.target.closest('[data-voice-tab]')) return;
    if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
      event.preventDefault();
      show(index + 1, { focus: true, announce: true });
      userStopped = true;
      stop();
    } else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
      event.preventDefault();
      show(index - 1, { focus: true, announce: true });
      userStopped = true;
      stop();
    } else if (event.key === 'Home') {
      event.preventDefault();
      show(0, { focus: true, announce: true });
      userStopped = true;
      stop();
    } else if (event.key === 'End') {
      event.preventDefault();
      show(quotes.length - 1, { focus: true, announce: true });
      userStopped = true;
      stop();
    }
  };

  const onEnter = () => {
    paused = true;
    stop();
  };
  const onLeave = () => {
    paused = false;
    start();
  };
  const onFocusOut = (event) => {
    if (!root.contains(event.relatedTarget)) onLeave();
  };

  root.addEventListener('click', onTabClick);
  root.addEventListener('keydown', onKey);
  root.addEventListener('mouseenter', onEnter);
  root.addEventListener('mouseleave', onLeave);
  root.addEventListener('focusin', onEnter);
  root.addEventListener('focusout', onFocusOut);

  show(index);
  start();

  return () => {
    stop();
    root.removeEventListener('click', onTabClick);
    root.removeEventListener('keydown', onKey);
    root.removeEventListener('mouseenter', onEnter);
    root.removeEventListener('mouseleave', onLeave);
    root.removeEventListener('focusin', onEnter);
    root.removeEventListener('focusout', onFocusOut);
  };
}

function bootVoices(root) {
  if (!root || root.dataset.booted === '1') return;
  root.dataset.booted = '1';
  const cleanups = [bootReveal(root), bootLedger(root)];
  root._artinkVoicesCleanup = () => {
    cleanups.forEach((fn) => fn && fn());
    root.dataset.booted = '0';
  };
}

function initAll() {
  document.querySelectorAll('[data-artink-voices]').forEach(bootVoices);
}

initAll();
document.addEventListener('shopify:section:load', (event) => {
  event.target.querySelectorAll('[data-artink-voices]').forEach(bootVoices);
});
document.addEventListener('shopify:section:unload', (event) => {
  event.target.querySelectorAll('[data-artink-voices]').forEach((root) => {
    root._artinkVoicesCleanup?.();
  });
});
